import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  Chip
} from '@mui/material';
import {
  BarChart as ChartIcon,
  TimerOutlined as TimerIcon,
  SupervisedUserCircle as UserIcon,
  Devices as DevicesIcon,
  VisibilityOutlined as VisibilityIcon,
  Dns as DnsIcon,
  AccessTime as AccessTimeIcon,
  DeviceHub as DeviceHubIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { format, formatDistanceToNow } from 'date-fns';
import axios from 'axios';
import { ShareAnalytics as ShareAnalyticsType } from '../../types/shares';

import { getDriveApiToken } from '../../lib/auth-token';
// Create API service with base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = getDriveApiToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface ShareAnalyticsProps {
  shareId: string;
}

const ShareAnalytics: React.FC<ShareAnalyticsProps> = ({ shareId }) => {
  const theme = useTheme();
  const [analyticsData, setAnalyticsData] = useState<ShareAnalyticsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shareId) return;

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/shares/${shareId}/analytics`);
        setAnalyticsData(response.data.analytics);
      } catch (err) {

        setError('Failed to fetch share analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [shareId]);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" width="100%" height={100} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rectangular" width="30%" height={80} />
          <Skeleton variant="rectangular" width="30%" height={80} />
          <Skeleton variant="rectangular" width="30%" height={80} />
        </Box>
        <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!analyticsData) {
    return (
      <Alert severity="info">
        No analytics data available for this share. Analytics will be collected when the share is accessed.
      </Alert>
    );
  }

  const getUserAgentIcon = (userAgent: string) => {
    const userAgentLower = userAgent.toLowerCase();
    
    if (userAgentLower.includes('android')) return '🤖';
    if (userAgentLower.includes('iphone') || userAgentLower.includes('ipad')) return '📱';
    if (userAgentLower.includes('windows')) return '🪟';
    if (userAgentLower.includes('mac')) return '🍎';
    if (userAgentLower.includes('linux')) return '🐧';
    return '🖥️';
  };

  const getUserAgentName = (userAgent: string) => {
    if (!userAgent) return 'Unknown';
    
    const userAgentLower = userAgent.toLowerCase();
    
    if (userAgentLower.includes('chrome')) return 'Chrome';
    if (userAgentLower.includes('firefox')) return 'Firefox';
    if (userAgentLower.includes('safari') && !userAgentLower.includes('chrome')) return 'Safari';
    if (userAgentLower.includes('edge')) return 'Edge';
    if (userAgentLower.includes('opera')) return 'Opera';
    if (userAgentLower.includes('android')) return 'Android';
    if (userAgentLower.includes('iphone') || userAgentLower.includes('ipad')) return 'iOS';
    
    return 'Other';
  };
  
  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <VisibilityIcon color="primary" sx={{ fontSize: 36, mb: 1 }} />
              <Typography variant="h4" component="div">
                {analyticsData.totalAccesses}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total views
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card elevation={0} variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <DeviceHubIcon color="primary" sx={{ fontSize: 36, mb: 1 }} />
              <Typography variant="h4" component="div">
                {analyticsData.uniqueIPs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique visitors
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card elevation={0} variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <AccessTimeIcon color="primary" sx={{ fontSize: 36, mb: 1 }} />
              <Typography variant="h4" component="div">
                {analyticsData.shareLink?.lastAccessedAt ? 
                  formatDistanceToNow(new Date(analyticsData.shareLink.lastAccessedAt), { addSuffix: true }) : 
                  'Never'
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last accessed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Typography variant="h6" gutterBottom>
        Device breakdown
      </Typography>
      <Paper variant="outlined" sx={{ mb: 3, p: 0 }}>
        <List sx={{ width: '100%', p: 0 }}>
          {Object.entries(analyticsData.userAgentCounts).map(([userAgent, count], index, array) => (
            <React.Fragment key={userAgent}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: theme.palette.background.default }}>
                    {getUserAgentIcon(userAgent)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary={getUserAgentName(userAgent)} 
                  secondary={`${count} access${count !== 1 ? 'es' : ''}`} 
                />
                <Chip 
                  label={`${Math.round((count / analyticsData.totalAccesses) * 100)}%`} 
                  size="small" 
                  variant="outlined" 
                />
              </ListItem>
              {index < array.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
          {Object.keys(analyticsData.userAgentCounts).length === 0 && (
            <ListItem>
              <ListItemText primary="No device data available yet" />
            </ListItem>
          )}
        </List>
      </Paper>
      
      <Typography variant="h6" gutterBottom>
        Recent accesses
      </Typography>
      <Paper variant="outlined" sx={{ mb: 3, p: 0 }}>
        <List sx={{ width: '100%', p: 0 }}>
          {analyticsData.recentAccesses.map((access, index, array) => (
            <React.Fragment key={access.id}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
                    <UserIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary={access.accessedByUser?.email || 'Anonymous visitor'} 
                  secondary={`${format(new Date(access.accessedAt), 'PPpp')} • IP: ${access.ipAddress || 'Unknown'}`} 
                />
              </ListItem>
              {index < array.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
          {analyticsData.recentAccesses.length === 0 && (
            <ListItem>
              <ListItemText primary="No recent accesses recorded" />
            </ListItem>
          )}
        </List>
      </Paper>
    </Box>
  );
};

export default ShareAnalytics; 